import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import ProfessionalLayout from '../../components/ProfessionalLayout';
import { useLanguage } from '../../contexts/LanguageContext';
import SEOHead from '../../components/SEOHead';
import { STATUS_LABELS } from '../../lib/constants';
import PackageStatusBadge from '../../components/PackageStatusBadge';
import { CountryRoute } from '../../components/CountryDisplay';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Package, Users, TrendingUp, Ship } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CountryAdminDashboard = () => {
  const { t, language } = useLanguage();
  const statusLabels = (STATUS_LABELS[language] || STATUS_LABELS.fr);
  const navigate = useNavigate();
  const [packages, setPackages] = useState([]);
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    const token = localStorage.getItem('token');
    try {
      const [packagesRes, partnersRes] = await Promise.all([
        axios.get(`${API}/packages`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/users?role=partner`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      // Filtrer les colis collectés - ils ne doivent apparaître que dans les carréaisons
      const nonCollectedPackages = packagesRes.data.filter(pkg => pkg.status !== 'collected');
      setPackages(nonCollectedPackages);
      setPartners(partnersRes.data);
    } catch (err) {
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <ProfessionalLayout>
        <p>{t('loading')}</p>
      </ProfessionalLayout>
    );
  }

  return (
    <ProfessionalLayout>
      <SEOHead title={t('seoDashboardTitle')} description={t('seoDashboardDesc')} noindex />
      <div className="space-y-6">
        {/* Statistics */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">{t('totalPackages')}</CardTitle>
              <Package className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{packages.length}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">{t('partners')}</CardTitle>
              <Users className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{partners.length}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">{t('inTransit')}</CardTitle>
              <TrendingUp className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {packages.filter(p => p.status === 'in_transit').length}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Packages List */}
        <Card>
          <CardHeader>
            <CardTitle>{t('recentPackages')}</CardTitle>
            <CardDescription>{t('manageCountryPackages')}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {packages.length === 0 ? (
                <p className="text-gray-500">{t('noPackages')}</p>
              ) : (
                packages.slice(0, 10).map(pkg => (
                  <div key={pkg.id} className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50">
                    <div className="flex-1">
                      <p className="font-medium">{pkg.oce_code}</p>
                      <p className="text-sm text-gray-600">
                        <CountryRoute origin={pkg.origin_country} destination={pkg.destination_country} size="sm" />
                      </p>
                      <p className="text-xs text-gray-500">{pkg.sender_name}</p>
                    </div>
                    <PackageStatusBadge status={pkg.status} size="sm" />
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </ProfessionalLayout>
  );
};

export default CountryAdminDashboard;